import { useEffect, useMemo, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    GlobalStyles,
    IconButton,
    LinearProgress,
    MenuItem,
    Paper,
    Stack,
    Switch,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';

import { SegmentedTabs } from '../admin/SegmentedTabs';
import { formatApiDateTime } from '../admin/dateTimeUtils';
import {
    examNames,
    examTaskTypes,
    getExamTaskTypeByApiType,
    getExamTaskTypeLabel,
    type ExamName,
} from '../examTasks/examTaskTypes';
import {
    archiveAdminStudyPlanProfile,
    cloneAdminStudyPlanProfile,
    createAdminStudyPlanProfile,
    getAdminStudyPlanProfile,
    getAdminStudyPlanProfiles,
    publishAdminStudyPlanProfile,
    upsertAdminStudyPlanCollection,
    validateAdminStudyPlanProfile,
    type AdminStudyPlanConfigCollection,
    type AdminStudyPlanMockRule,
    type AdminStudyPlanMultiplierRule,
    type AdminStudyPlanProfile,
    type AdminStudyPlanProfileDetail,
    type AdminStudyPlanReadinessStatusRule,
    type AdminStudyPlanReadinessWeightRule,
    type AdminStudyPlanSkillMarkRangeRule,
    type AdminStudyPlanTaskDailyRequirementRule,
    type AdminStudyPlanTaskRule,
    type AdminStudyPlanTaskScoreThresholdRule,
    type AdminStudyPlanTaskSkillWeight,
    type AdminStudyPlanValidation,
    type StudyPlanSkill,
} from '../admin/adminStudyPlanAlgorithmApi';

type ConfigSection =
    | 'profiles'
    | 'skillMarkRanges'
    | 'taskRules'
    | 'taskDailyRequirements'
    | 'taskSkillWeights'
    | 'multiplierRules'
    | 'mockRules'
    | 'taskScoreThresholds'
    | 'readinessWeights'
    | 'readinessStatuses';

type EditableConfigSection = Exclude<ConfigSection, 'profiles'>;
type EditableRecord = Record<string, unknown> & { _clientId?: string };
type ExamKindFilter = '' | ExamName;
type SkillFilter = '' | StudyPlanSkill;

type OptionItem = {
    value: string;
    label: string;
};

type ConfigColumn = {
    key: string;
    label: string;
    kind: 'text' | 'multiline' | 'number' | 'boolean' | 'select';
    options?: OptionItem[];
    nullable?: boolean;
    width?: number;
    min?: number;
    step?: string;
};

type ConfigSectionMeta = {
    key: ConfigSection;
    label: string;
    collection?: AdminStudyPlanConfigCollection;
};

const configSections: ConfigSectionMeta[] = [
    { key: 'profiles', label: 'Profiles' },
    { key: 'skillMarkRanges', label: 'Skill Ranges', collection: 'skillMarkRanges' },
    { key: 'taskRules', label: 'Task Rules', collection: 'taskRules' },
    { key: 'taskDailyRequirements', label: 'Daily Counts', collection: 'taskDailyRequirements' },
    { key: 'taskSkillWeights', label: 'Skill Weights', collection: 'taskSkillWeights' },
    { key: 'multiplierRules', label: 'Multipliers', collection: 'multiplierRules' },
    { key: 'mockRules', label: 'Mock Rules', collection: 'mockRules' },
    { key: 'taskScoreThresholds', label: 'Score Thresholds', collection: 'taskScoreThresholds' },
    { key: 'readinessWeights', label: 'Readiness Weights', collection: 'readinessWeights' },
    { key: 'readinessStatuses', label: 'Readiness Statuses', collection: 'readinessStatuses' },
];

const skillOptions: OptionItem[] = ['Speaking', 'Writing', 'Reading', 'Listening'].map((skill) => ({
    value: skill,
    label: skill,
}));

const examNameOptions: OptionItem[] = examNames.map((examName) => ({
    value: examName,
    label: examName,
}));

const examKindFilterOptions: OptionItem[] = [
    { value: '', label: 'All Exams' },
    ...examNameOptions,
];

const skillFilterOptions: OptionItem[] = [
    { value: '', label: 'All Skills' },
    ...skillOptions,
];

const priorityBandOptions: OptionItem[] = ['Critical', 'High', 'Average', 'Low', 'OptionalLeave'].map(toOption);
const compressionBehaviorOptions: OptionItem[] = ['Preserve', 'ReduceLast', 'DropWhenTight'].map(toOption);
const retryPolicyOptions: OptionItem[] = ['LatestScoreOnly', 'NoRetakeCredit', 'FullRetakeCredit'].map(toOption);
const combineStrategyOptions: OptionItem[] = ['MaxAcrossSkills', 'Max', 'Sum', 'SumCapped'].map(toOption);
const ruleScopeOptions: OptionItem[] = ['FullMock', 'SectionMock'].map(toOption);
const multiplierRuleTypeOptions: OptionItem[] = ['DaysUntilExam', 'Availability', 'Recency', 'Intensity', 'MockGap'].map(toOption);
const scorePickStrategyOptions: OptionItem[] = ['OverallScoreOutOf90', 'AverageDimensionScoreOutOf90', 'LatestScoreOutOf90'].map(toOption);
const readinessScopeOptions: OptionItem[] = ['Task', 'Section', 'Overall'].map(toOption);
const severityOptions: OptionItem[] = ['Info', 'Warning', 'Risk', 'Success'].map(toOption);
const confidenceOptions: OptionItem[] = ['', 'Low', 'Medium', 'High'].map((value) => ({
    value,
    label: value || 'None',
}));

const emptyDrafts: Record<EditableConfigSection, EditableRecord[]> = {
    skillMarkRanges: [],
    taskRules: [],
    taskDailyRequirements: [],
    taskSkillWeights: [],
    multiplierRules: [],
    mockRules: [],
    taskScoreThresholds: [],
    readinessWeights: [],
    readinessStatuses: [],
};

function toOption(value: string): OptionItem {
    return { value, label: formatEnumLabel(value) };
}

function formatEnumLabel(value: string): string {
    return value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim();
}

function createClientId(): string {
    return `client-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function withClientIds<T extends object>(items: T[]): EditableRecord[] {
    return items.map((item) => ({ ...(item as Record<string, unknown>), _clientId: createClientId() }));
}

function stripClientFields<T>(items: EditableRecord[]): T[] {
    return items.map((item) => {
        const next = { ...item };
        delete next._clientId;
        return next as T;
    });
}

function getSectionFromParam(value: string | undefined): ConfigSection {
    return configSections.some((section) => section.key === value) ? (value as ConfigSection) : 'profiles';
}

function getCollectionMeta(section: ConfigSection): ConfigSectionMeta | null {
    return configSections.find((item) => item.key === section) ?? null;
}

function getTaskLabel(examTaskType: string, examName?: ExamName): string {
    const taskType = getExamTaskTypeByApiType(examTaskType);
    return getExamTaskTypeLabel(taskType, examName) ?? examTaskType;
}

function asText(value: unknown): string {
    return value === null || value === undefined ? '' : String(value);
}

function asNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const next = Number(value);
    return Number.isFinite(next) ? next : null;
}

function optionFromNullable(value: string | null | undefined, label: string): OptionItem {
    return { value: value ?? '', label };
}

function getTaskRuleOptions(detail: AdminStudyPlanProfileDetail | null): OptionItem[] {
    return (detail?.taskRules ?? [])
        .filter((item) => item.studyPlanTaskRuleId)
        .map((item) => ({
            value: item.studyPlanTaskRuleId ?? '',
            label: `${item.examKind} - ${getTaskLabel(item.examTaskType, item.examKind)}`,
        }));
}

function getSkillRangeOptions(detail: AdminStudyPlanProfileDetail | null): OptionItem[] {
    return (detail?.skillMarkRanges ?? [])
        .filter((item) => item.studyPlanSkillMarkRangeRuleId)
        .map((item) => ({
            value: item.studyPlanSkillMarkRangeRuleId ?? '',
            label: `${item.examKind} - ${item.skill} - ${item.rangeKey}`,
        }));
}

function getExamTaskOptions(): OptionItem[] {
    return examTaskTypes.map((item) => ({
        value: item.apiType,
        label: `${item.category} - ${item.label}`,
    }));
}

function getProfileId(detail: AdminStudyPlanProfileDetail | null): string {
    return detail?.profile.studyPlanAlgorithmProfileId ?? '';
}

function getFirstTaskRule(detail: AdminStudyPlanProfileDetail | null, preferredExamKind?: ExamKindFilter): AdminStudyPlanTaskRule | null {
    return detail?.taskRules.find((item) => item.studyPlanTaskRuleId && (!preferredExamKind || item.examKind === preferredExamKind)) ??
        detail?.taskRules.find((item) => item.studyPlanTaskRuleId) ??
        null;
}

function getFirstSkillRange(detail: AdminStudyPlanProfileDetail | null, preferredExamKind?: ExamKindFilter): AdminStudyPlanSkillMarkRangeRule | null {
    return detail?.skillMarkRanges.find((item) => item.studyPlanSkillMarkRangeRuleId && (!preferredExamKind || item.examKind === preferredExamKind)) ??
        detail?.skillMarkRanges.find((item) => item.studyPlanSkillMarkRangeRuleId) ??
        null;
}

function createDefaultRow(section: EditableConfigSection, detail: AdminStudyPlanProfileDetail | null, preferredExamKind?: ExamKindFilter): EditableRecord {
    const profileId = getProfileId(detail);
    const firstTask = getFirstTaskRule(detail, preferredExamKind);
    const firstRange = getFirstSkillRange(detail, preferredExamKind);
    const examKind = (preferredExamKind || firstTask?.examKind || firstRange?.examKind || 'Academic') as ExamName;
    const skill = (firstRange?.skill ?? 'Speaking') as StudyPlanSkill;
    const examTaskType = firstTask?.examTaskType ?? 'ReadAloud';

    switch (section) {
        case 'skillMarkRanges':
            return {
                _clientId: createClientId(),
                studyPlanAlgorithmProfileId: profileId,
                examKind,
                skill,
                rangeKey: 'Custom',
                displayName: 'Custom',
                minScore: 0,
                maxScore: 90,
                rangeWeight: 1,
                displayOrder: 999,
                description: '',
            };
        case 'taskRules':
            return {
                _clientId: createClientId(),
                studyPlanAlgorithmProfileId: profileId,
                examKind,
                examTaskType,
                displayName: getTaskLabel(examTaskType, examKind),
                estimatedMinutesPerItem: 2,
                priorityBand: 'Average',
                basePriorityWeight: 50,
                prioritySortOrder: 999,
                compressionBehavior: 'ReduceLast',
                completionRequiresScored: true,
                allowMockItemCredit: false,
                countDistinctExamTaskOnly: true,
                retryCreditPolicy: 'LatestScoreOnly',
                isRequiredInPlan: true,
                settingsJson: {},
            };
        case 'taskDailyRequirements':
            return {
                _clientId: createClientId(),
                studyPlanAlgorithmProfileId: profileId,
                studyPlanTaskRuleId: firstTask?.studyPlanTaskRuleId ?? '',
                studyPlanSkillMarkRangeRuleId: firstRange?.studyPlanSkillMarkRangeRuleId ?? '',
                examKind,
                examTaskType,
                driverSkill: skill,
                rangeKey: firstRange?.rangeKey ?? '',
                minDailyCount: 0,
                recommendedDailyCount: 1,
                maxNormalDailyCount: null,
                weeklyMinDays: null,
                combineStrategy: 'MaxAcrossSkills',
                isEnabled: true,
            };
        case 'taskSkillWeights':
            return {
                _clientId: createClientId(),
                studyPlanTaskRuleId: firstTask?.studyPlanTaskRuleId ?? '',
                skill,
                weight: 1,
            };
        case 'multiplierRules':
            return {
                _clientId: createClientId(),
                studyPlanAlgorithmProfileId: profileId,
                ruleType: 'Intensity',
                ruleKey: 'Custom',
                minValue: null,
                maxValue: null,
                multiplier: 1,
                settingsJson: {},
                displayOrder: 999,
            };
        case 'mockRules':
            return {
                _clientId: createClientId(),
                studyPlanAlgorithmProfileId: profileId,
                ruleScope: 'SectionMock',
                examKind,
                skill,
                studyPlanSkillMarkRangeRuleId: firstRange?.studyPlanSkillMarkRangeRuleId ?? '',
                rangeKey: firstRange?.rangeKey ?? '',
                minDaysUntilExam: 0,
                maxDaysUntilExam: 365,
                fullMockMinimumCount: 0,
                fullMockMaximumCount: null,
                sectionMockMinimumCount: 1,
                preferredFirstMockDayOffset: null,
                preferredFinalMockDaysBeforeExam: 3,
                fullMockReviewMinutes: 75,
                sectionMockReviewMinutes: 35,
                combineStrategy: 'MaxAcrossSkills',
                countDistinctMockTestOnly: true,
                retakeCreditPolicy: 'LatestScoreOnly',
                settingsJson: {},
            };
        case 'taskScoreThresholds':
            return {
                _clientId: createClientId(),
                studyPlanAlgorithmProfileId: profileId,
                studyPlanTaskRuleId: firstTask?.studyPlanTaskRuleId ?? '',
                studyPlanSkillMarkRangeRuleId: firstRange?.studyPlanSkillMarkRangeRuleId ?? '',
                examKind,
                examTaskType,
                skill,
                rangeKey: firstRange?.rangeKey ?? '',
                requiredScoreOutOf90: 70,
                strongScoreOutOf90: 80,
                minimumConsistencyPercent: 70,
                minimumRecentDistinctItems: 5,
                rollingDayWindow: 30,
                rollingItemWindow: 20,
                scorePickStrategy: 'OverallScoreOutOf90',
                dimensionWeightsJson: {},
                isEnabled: true,
            };
        case 'readinessWeights':
            return {
                _clientId: createClientId(),
                studyPlanAlgorithmProfileId: profileId,
                ruleKey: 'Custom',
                scope: 'Task',
                examKind,
                skill,
                studyPlanTaskRuleId: firstTask?.studyPlanTaskRuleId ?? '',
                studyPlanSkillMarkRangeRuleId: firstRange?.studyPlanSkillMarkRangeRuleId ?? '',
                effortWeight: 0.15,
                outputWeight: 0.5,
                mockWeight: 0,
                trendWeight: 0.15,
                stabilityWeight: 0.15,
                recencyWeight: 0.05,
                minimumEvidenceCount: 3,
                mockFreshnessDays: null,
                settingsJson: {},
                isEnabled: true,
            };
        case 'readinessStatuses':
            return {
                _clientId: createClientId(),
                studyPlanAlgorithmProfileId: profileId,
                scope: 'Task',
                statusKey: 'Custom',
                minReadinessPercent: null,
                maxReadinessPercent: null,
                minEffortPercent: null,
                minOutputPercent: null,
                maxScoreGapPoints: null,
                requiresRecentMock: false,
                confidenceFloor: null,
                severity: 'Info',
                messageTemplate: '',
                displayOrder: 999,
                isEnabled: true,
            };
    }
}

function getColumns(section: EditableConfigSection, detail: AdminStudyPlanProfileDetail | null): ConfigColumn[] {
    const taskRuleOptions = getTaskRuleOptions(detail);
    const skillRangeOptions = getSkillRangeOptions(detail);
    const examTaskOptions = getExamTaskOptions();

    switch (section) {
        case 'skillMarkRanges':
            return [
                { key: 'examKind', label: 'Exam', kind: 'select', options: examNameOptions, width: 130 },
                { key: 'skill', label: 'Skill', kind: 'select', options: skillOptions, width: 140 },
                { key: 'rangeKey', label: 'Range Key', kind: 'text', width: 150 },
                { key: 'displayName', label: 'Display Name', kind: 'text', width: 180 },
                { key: 'minScore', label: 'Min', kind: 'number', min: 0, width: 110 },
                { key: 'maxScore', label: 'Max', kind: 'number', min: 0, width: 110 },
                { key: 'rangeWeight', label: 'Weight', kind: 'number', min: 0, step: '0.01', width: 120 },
                { key: 'displayOrder', label: 'Order', kind: 'number', width: 110 },
                { key: 'description', label: 'Description', kind: 'multiline', nullable: true, width: 260 },
            ];
        case 'taskRules':
            return [
                { key: 'examKind', label: 'Exam', kind: 'select', options: examNameOptions, width: 130 },
                { key: 'examTaskType', label: 'Task Type', kind: 'select', options: examTaskOptions, width: 230 },
                { key: 'displayName', label: 'Display Name', kind: 'text', width: 210 },
                { key: 'estimatedMinutesPerItem', label: 'Min / Item', kind: 'number', min: 0, step: '0.1', width: 120 },
                { key: 'priorityBand', label: 'Priority', kind: 'select', options: priorityBandOptions, width: 150 },
                { key: 'basePriorityWeight', label: 'Priority Weight', kind: 'number', min: 0, width: 140 },
                { key: 'prioritySortOrder', label: 'Sort', kind: 'number', width: 100 },
                { key: 'compressionBehavior', label: 'Compression', kind: 'select', options: compressionBehaviorOptions, width: 170 },
                { key: 'completionRequiresScored', label: 'Needs Score', kind: 'boolean', width: 120 },
                { key: 'allowMockItemCredit', label: 'Mock Item Credit', kind: 'boolean', width: 150 },
                { key: 'countDistinctExamTaskOnly', label: 'Distinct Only', kind: 'boolean', width: 130 },
                { key: 'retryCreditPolicy', label: 'Retry Policy', kind: 'select', options: retryPolicyOptions, width: 170 },
                { key: 'isRequiredInPlan', label: 'Required', kind: 'boolean', width: 110 },
            ];
        case 'taskDailyRequirements':
            return [
                { key: 'examKind', label: 'Exam', kind: 'select', options: examNameOptions, width: 130 },
                { key: 'examTaskType', label: 'Task Type', kind: 'select', options: examTaskOptions, width: 230 },
                { key: 'studyPlanTaskRuleId', label: 'Task Rule', kind: 'select', options: taskRuleOptions, width: 260 },
                { key: 'driverSkill', label: 'Driver Skill', kind: 'select', options: skillOptions, width: 150 },
                { key: 'rangeKey', label: 'Range Key', kind: 'text', width: 140 },
                { key: 'studyPlanSkillMarkRangeRuleId', label: 'Skill Range', kind: 'select', options: skillRangeOptions, width: 260 },
                { key: 'minDailyCount', label: 'Min / Day', kind: 'number', min: 0, width: 120 },
                { key: 'recommendedDailyCount', label: 'Recommended / Day', kind: 'number', min: 0, width: 150 },
                { key: 'maxNormalDailyCount', label: 'Max Normal', kind: 'number', min: 0, nullable: true, width: 130 },
                { key: 'weeklyMinDays', label: 'Weekly Days', kind: 'number', min: 0, nullable: true, width: 130 },
                { key: 'combineStrategy', label: 'Combine', kind: 'select', options: combineStrategyOptions, width: 170 },
                { key: 'isEnabled', label: 'Enabled', kind: 'boolean', width: 110 },
            ];
        case 'taskSkillWeights':
            return [
                { key: 'studyPlanTaskRuleId', label: 'Task Rule', kind: 'select', options: taskRuleOptions, width: 280 },
                { key: 'skill', label: 'Skill', kind: 'select', options: skillOptions, width: 150 },
                { key: 'weight', label: 'Weight', kind: 'number', min: 0, step: '0.01', width: 130 },
            ];
        case 'multiplierRules':
            return [
                { key: 'ruleType', label: 'Type', kind: 'select', options: multiplierRuleTypeOptions, width: 170 },
                { key: 'ruleKey', label: 'Key', kind: 'text', width: 220 },
                { key: 'minValue', label: 'Min', kind: 'number', nullable: true, width: 120 },
                { key: 'maxValue', label: 'Max', kind: 'number', nullable: true, width: 120 },
                { key: 'multiplier', label: 'Multiplier', kind: 'number', min: 0, step: '0.01', width: 130 },
                { key: 'displayOrder', label: 'Order', kind: 'number', width: 110 },
            ];
        case 'mockRules':
            return [
                { key: 'ruleScope', label: 'Scope', kind: 'select', options: ruleScopeOptions, width: 150 },
                { key: 'examKind', label: 'Exam', kind: 'select', options: examNameOptions, width: 130 },
                { key: 'skill', label: 'Skill', kind: 'select', options: [optionFromNullable(null, 'None'), ...skillOptions], nullable: true, width: 150 },
                { key: 'rangeKey', label: 'Range Key', kind: 'text', nullable: true, width: 140 },
                { key: 'studyPlanSkillMarkRangeRuleId', label: 'Skill Range', kind: 'select', options: [optionFromNullable(null, 'None'), ...skillRangeOptions], nullable: true, width: 260 },
                { key: 'minDaysUntilExam', label: 'Min Days', kind: 'number', min: 0, width: 120 },
                { key: 'maxDaysUntilExam', label: 'Max Days', kind: 'number', min: 0, width: 120 },
                { key: 'fullMockMinimumCount', label: 'Full Min', kind: 'number', min: 0, width: 120 },
                { key: 'fullMockMaximumCount', label: 'Full Max', kind: 'number', min: 0, nullable: true, width: 120 },
                { key: 'sectionMockMinimumCount', label: 'Section Min', kind: 'number', min: 0, width: 130 },
                { key: 'preferredFirstMockDayOffset', label: 'First Offset', kind: 'number', nullable: true, width: 130 },
                { key: 'preferredFinalMockDaysBeforeExam', label: 'Final Before', kind: 'number', nullable: true, width: 130 },
                { key: 'fullMockReviewMinutes', label: 'Full Review', kind: 'number', min: 0, width: 130 },
                { key: 'sectionMockReviewMinutes', label: 'Section Review', kind: 'number', min: 0, width: 140 },
                { key: 'combineStrategy', label: 'Combine', kind: 'select', options: combineStrategyOptions, width: 170 },
                { key: 'countDistinctMockTestOnly', label: 'Distinct Mock', kind: 'boolean', width: 140 },
                { key: 'retakeCreditPolicy', label: 'Retake Policy', kind: 'select', options: retryPolicyOptions, width: 170 },
            ];
        case 'taskScoreThresholds':
            return [
                { key: 'examKind', label: 'Exam', kind: 'select', options: examNameOptions, width: 130 },
                { key: 'examTaskType', label: 'Task Type', kind: 'select', options: examTaskOptions, width: 230 },
                { key: 'studyPlanTaskRuleId', label: 'Task Rule', kind: 'select', options: taskRuleOptions, width: 260 },
                { key: 'skill', label: 'Skill', kind: 'select', options: skillOptions, width: 150 },
                { key: 'rangeKey', label: 'Range Key', kind: 'text', nullable: true, width: 140 },
                { key: 'studyPlanSkillMarkRangeRuleId', label: 'Skill Range', kind: 'select', options: [optionFromNullable(null, 'None'), ...skillRangeOptions], nullable: true, width: 260 },
                { key: 'requiredScoreOutOf90', label: 'Required', kind: 'number', min: 0, step: '0.1', width: 120 },
                { key: 'strongScoreOutOf90', label: 'Strong', kind: 'number', min: 0, step: '0.1', nullable: true, width: 120 },
                { key: 'minimumConsistencyPercent', label: 'Consistency %', kind: 'number', min: 0, step: '0.1', width: 140 },
                { key: 'minimumRecentDistinctItems', label: 'Evidence Items', kind: 'number', min: 0, width: 140 },
                { key: 'rollingDayWindow', label: 'Rolling Days', kind: 'number', min: 0, width: 130 },
                { key: 'rollingItemWindow', label: 'Rolling Items', kind: 'number', min: 0, width: 130 },
                { key: 'scorePickStrategy', label: 'Score Strategy', kind: 'select', options: scorePickStrategyOptions, width: 230 },
                { key: 'isEnabled', label: 'Enabled', kind: 'boolean', width: 110 },
            ];
        case 'readinessWeights':
            return [
                { key: 'ruleKey', label: 'Rule Key', kind: 'text', width: 220 },
                { key: 'scope', label: 'Scope', kind: 'select', options: readinessScopeOptions, width: 130 },
                { key: 'examKind', label: 'Exam', kind: 'select', options: [optionFromNullable(null, 'Any'), ...examNameOptions], nullable: true, width: 130 },
                { key: 'skill', label: 'Skill', kind: 'select', options: [optionFromNullable(null, 'Any'), ...skillOptions], nullable: true, width: 140 },
                { key: 'studyPlanTaskRuleId', label: 'Task Rule', kind: 'select', options: [optionFromNullable(null, 'Any'), ...taskRuleOptions], nullable: true, width: 260 },
                { key: 'studyPlanSkillMarkRangeRuleId', label: 'Skill Range', kind: 'select', options: [optionFromNullable(null, 'Any'), ...skillRangeOptions], nullable: true, width: 260 },
                { key: 'effortWeight', label: 'Effort', kind: 'number', min: 0, step: '0.01', width: 110 },
                { key: 'outputWeight', label: 'Output', kind: 'number', min: 0, step: '0.01', width: 110 },
                { key: 'mockWeight', label: 'Mock', kind: 'number', min: 0, step: '0.01', width: 110 },
                { key: 'trendWeight', label: 'Trend', kind: 'number', min: 0, step: '0.01', width: 110 },
                { key: 'stabilityWeight', label: 'Stability', kind: 'number', min: 0, step: '0.01', width: 120 },
                { key: 'recencyWeight', label: 'Recency', kind: 'number', min: 0, step: '0.01', width: 120 },
                { key: 'minimumEvidenceCount', label: 'Evidence Min', kind: 'number', min: 0, width: 130 },
                { key: 'mockFreshnessDays', label: 'Mock Fresh Days', kind: 'number', min: 0, nullable: true, width: 150 },
                { key: 'isEnabled', label: 'Enabled', kind: 'boolean', width: 110 },
            ];
        case 'readinessStatuses':
            return [
                { key: 'scope', label: 'Scope', kind: 'select', options: readinessScopeOptions, width: 130 },
                { key: 'statusKey', label: 'Status Key', kind: 'text', width: 220 },
                { key: 'minReadinessPercent', label: 'Min Ready %', kind: 'number', min: 0, nullable: true, width: 130 },
                { key: 'maxReadinessPercent', label: 'Max Ready %', kind: 'number', min: 0, nullable: true, width: 130 },
                { key: 'minEffortPercent', label: 'Min Effort %', kind: 'number', min: 0, nullable: true, width: 130 },
                { key: 'minOutputPercent', label: 'Min Output %', kind: 'number', min: 0, nullable: true, width: 130 },
                { key: 'maxScoreGapPoints', label: 'Max Gap', kind: 'number', min: 0, nullable: true, width: 120 },
                { key: 'requiresRecentMock', label: 'Needs Mock', kind: 'boolean', width: 120 },
                { key: 'confidenceFloor', label: 'Confidence', kind: 'select', options: confidenceOptions, nullable: true, width: 140 },
                { key: 'severity', label: 'Severity', kind: 'select', options: severityOptions, width: 130 },
                { key: 'displayOrder', label: 'Order', kind: 'number', width: 110 },
                { key: 'messageTemplate', label: 'Message', kind: 'multiline', nullable: true, width: 360 },
                { key: 'isEnabled', label: 'Enabled', kind: 'boolean', width: 110 },
            ];
    }
}

function getTaskRuleExamKind(detail: AdminStudyPlanProfileDetail | null, taskRuleId: unknown): string {
    const id = asText(taskRuleId);
    return detail?.taskRules.find((item) => item.studyPlanTaskRuleId === id)?.examKind ?? '';
}

function getTaskRuleSkill(detail: AdminStudyPlanProfileDetail | null, taskRuleId: unknown): string {
    const id = asText(taskRuleId);
    const taskRule = detail?.taskRules.find((item) => item.studyPlanTaskRuleId === id);
    const taskType = getExamTaskTypeByApiType(taskRule?.examTaskType ?? '');
    return taskType?.category ?? '';
}

function getSkillRangeExamKind(detail: AdminStudyPlanProfileDetail | null, skillRangeRuleId: unknown): string {
    const id = asText(skillRangeRuleId);
    return detail?.skillMarkRanges.find((item) => item.studyPlanSkillMarkRangeRuleId === id)?.examKind ?? '';
}

function getSkillRangeSkill(detail: AdminStudyPlanProfileDetail | null, skillRangeRuleId: unknown): string {
    const id = asText(skillRangeRuleId);
    return detail?.skillMarkRanges.find((item) => item.studyPlanSkillMarkRangeRuleId === id)?.skill ?? '';
}

function rowMatchesExamKindFilter(
    section: EditableConfigSection,
    row: EditableRecord,
    examKindFilter: ExamKindFilter,
    detail: AdminStudyPlanProfileDetail | null,
): boolean {
    if (!examKindFilter) return true;

    const directExamKind = asText(row.examKind);
    if (directExamKind) return directExamKind === examKindFilter;

    if (section === 'taskSkillWeights') {
        return getTaskRuleExamKind(detail, row.studyPlanTaskRuleId) === examKindFilter;
    }

    const linkedTaskExamKind = getTaskRuleExamKind(detail, row.studyPlanTaskRuleId);
    if (linkedTaskExamKind) return linkedTaskExamKind === examKindFilter;

    const linkedRangeExamKind = getSkillRangeExamKind(detail, row.studyPlanSkillMarkRangeRuleId);
    if (linkedRangeExamKind) return linkedRangeExamKind === examKindFilter;

    return true;
}

function rowMatchesSkillFilter(
    section: EditableConfigSection,
    row: EditableRecord,
    skillFilter: SkillFilter,
    detail: AdminStudyPlanProfileDetail | null,
): boolean {
    if (!skillFilter) return true;

    const directSkill = asText(row.skill || row.driverSkill);
    if (directSkill) return directSkill === skillFilter;

    const linkedTaskSkill = getTaskRuleSkill(detail, row.studyPlanTaskRuleId);
    if (linkedTaskSkill) return linkedTaskSkill === skillFilter;

    const linkedRangeSkill = getSkillRangeSkill(detail, row.studyPlanSkillMarkRangeRuleId);
    if (linkedRangeSkill) return linkedRangeSkill === skillFilter;

    const examTaskType = asText(row.examTaskType);
    if (examTaskType) {
        const taskType = getExamTaskTypeByApiType(examTaskType);
        if (taskType?.category) return taskType.category === skillFilter;
    }

    if (section === 'multiplierRules' || section === 'readinessStatuses') return true;

    return true;
}

function getFilteredRows(
    section: EditableConfigSection,
    rows: EditableRecord[],
    examKindFilter: ExamKindFilter,
    skillFilter: SkillFilter,
    detail: AdminStudyPlanProfileDetail | null,
): EditableRecord[] {
    return rows.filter((row) => (
        rowMatchesExamKindFilter(section, row, examKindFilter, detail) &&
        rowMatchesSkillFilter(section, row, skillFilter, detail)
    ));
}

function mergeFilteredRows(
    section: EditableConfigSection,
    currentRows: EditableRecord[],
    nextFilteredRows: EditableRecord[],
    examKindFilter: ExamKindFilter,
    skillFilter: SkillFilter,
    detail: AdminStudyPlanProfileDetail | null,
): EditableRecord[] {
    if (!examKindFilter && !skillFilter) return nextFilteredRows;

    const nextByClientId = new Map(nextFilteredRows.map((row) => [asText(row._clientId), row]));
    const rowMatchesActiveFilters = (row: EditableRecord) => (
        rowMatchesExamKindFilter(section, row, examKindFilter, detail) &&
        rowMatchesSkillFilter(section, row, skillFilter, detail)
    );
    const retainedRows = currentRows
        .filter((row) => !rowMatchesActiveFilters(row))
        .concat(
            currentRows
                .filter(rowMatchesActiveFilters)
                .map((row) => nextByClientId.get(asText(row._clientId)))
                .filter((row): row is EditableRecord => Boolean(row)),
        );

    const retainedClientIds = new Set(retainedRows.map((row) => asText(row._clientId)));
    return retainedRows.concat(nextFilteredRows.filter((row) => !retainedClientIds.has(asText(row._clientId))));
}

function SummaryCard({ label, value, helper }: { label: string; value: string; helper: string }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 2,
                minWidth: 0,
                borderColor: 'rgba(148, 163, 184, 0.24)',
                backgroundColor: '#ffffff',
            }}
        >
            <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 800 }}>
                {label}
            </Typography>
            <Typography variant="h5" sx={{ color: '#0f172a', fontWeight: 850 }}>
                {value}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
                {helper}
            </Typography>
        </Paper>
    );
}

function StatusChip({ status }: { status: string }) {
    const color = status === 'Active' ? 'success' : status === 'Archived' ? 'default' : 'warning';
    return <Chip size="small" color={color} variant="outlined" label={status || 'Unknown'} />;
}

function SectionLoadingPanel({ label }: { label: string }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 2,
                p: 3,
                minHeight: 220,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 2,
                backgroundColor: '#f8fafc',
            }}
        >
            <Typography sx={{ color: '#0f172a', fontWeight: 850 }}>
                Loading {label}
            </Typography>
            <LinearProgress />
            <Typography variant="body2" sx={{ color: '#64748b' }}>
                Preparing the selected configuration grid.
            </Typography>
        </Paper>
    );
}

function renderEditableCell(
    row: EditableRecord,
    column: ConfigColumn,
    onChange: (key: string, value: unknown) => void,
) {
    const value = row[column.key];

    if (column.kind === 'boolean') {
        return (
            <Switch
                size="small"
                checked={Boolean(value)}
                onChange={(event) => onChange(column.key, event.target.checked)}
            />
        );
    }

    if (column.kind === 'select') {
        return (
            <TextField
                select
                size="small"
                fullWidth
                value={asText(value)}
                onChange={(event) => onChange(column.key, column.nullable && event.target.value === '' ? null : event.target.value)}
                SelectProps={{ native: true }}
            >
                {(column.options ?? []).map((option) => (
                    <option key={`${column.key}-${option.value || 'blank'}`} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </TextField>
        );
    }

    if (column.kind === 'number') {
        return (
            <TextField
                type="number"
                size="small"
                fullWidth
                value={value === null || value === undefined ? '' : String(value)}
                inputProps={{ min: column.min, step: column.step ?? 'any' }}
                onChange={(event) => onChange(column.key, column.nullable && event.target.value.trim() === '' ? null : asNumber(event.target.value) ?? 0)}
            />
        );
    }

    return (
        <TextField
            size="small"
            fullWidth
            multiline={column.kind === 'multiline'}
            minRows={column.kind === 'multiline' ? 2 : undefined}
            value={asText(value)}
            onChange={(event) => onChange(column.key, column.nullable && event.target.value.trim() === '' ? null : event.target.value)}
        />
    );
}

function renderReadonlyCell(row: EditableRecord, column: ConfigColumn) {
    const value = row[column.key];

    if (column.kind === 'boolean') {
        return (
            <Chip
                size="small"
                color={value ? 'success' : 'default'}
                variant={value ? 'filled' : 'outlined'}
                label={value ? 'Yes' : 'No'}
            />
        );
    }

    if (column.kind === 'select') {
        const option = (column.options ?? []).find((item) => item.value === asText(value));
        return option?.label || (column.nullable ? 'Any' : '-');
    }

    return asText(value) || '-';
}

function getDisplayColumnWidth(section: EditableConfigSection, column: ConfigColumn): number {
    if (section === 'taskDailyRequirements') {
        const widths: Record<string, number> = {
            examKind: 90,
            examTaskType: 190,
            studyPlanTaskRuleId: 210,
            driverSkill: 110,
            rangeKey: 190,
            studyPlanSkillMarkRangeRuleId: 220,
            minDailyCount: 88,
            recommendedDailyCount: 118,
            maxNormalDailyCount: 105,
            weeklyMinDays: 95,
            combineStrategy: 105,
            isEnabled: 90,
        };
        return widths[column.key] ?? column.width ?? 140;
    }

    if (section === 'mockRules') {
        const widths: Record<string, number> = {
            ruleScope: 105,
            examKind: 90,
            skill: 100,
            rangeKey: 125,
            studyPlanSkillMarkRangeRuleId: 190,
            minDaysUntilExam: 92,
            maxDaysUntilExam: 92,
            fullMockMinimumCount: 92,
            fullMockMaximumCount: 92,
            sectionMockMinimumCount: 104,
            preferredFirstMockDayOffset: 102,
            preferredFinalMockDaysBeforeExam: 102,
            fullMockReviewMinutes: 102,
            sectionMockReviewMinutes: 112,
            combineStrategy: 105,
            countDistinctMockTestOnly: 105,
            retakeCreditPolicy: 120,
        };
        return widths[column.key] ?? column.width ?? 130;
    }

    if (section === 'taskScoreThresholds' || section === 'readinessWeights' || section === 'readinessStatuses') {
        if (column.kind === 'number') return Math.min(column.width ?? 110, 108);
        if (column.kind === 'boolean') return 90;
        if (column.key.includes('RuleId') || column.key.includes('RangeRuleId')) return 190;
        if (column.key === 'messageTemplate') return 260;
        return Math.min(column.width ?? 140, 170);
    }

    if (column.kind === 'number') return Math.min(column.width ?? 110, 105);
    if (column.kind === 'boolean') return 90;
    if (column.key.includes('RuleId') || column.key.includes('RangeRuleId')) return 190;
    return Math.min(column.width ?? 140, 190);
}

function ConfigTable({
    section,
    rows,
    columns,
    isSaving,
    onRowsChange,
    onAdd,
    onSave,
}: {
    section: EditableConfigSection;
    rows: EditableRecord[];
    columns: ConfigColumn[];
    isSaving: boolean;
    onRowsChange: (rows: EditableRecord[]) => void;
    onAdd: () => void;
    onSave: () => void;
}) {
    const label = configSections.find((item) => item.key === section)?.label ?? section;
    const [editingRows, setEditingRows] = useState<Set<string>>(() => new Set());
    const columnWidths = columns.map((column) => getDisplayColumnWidth(section, column));
    const totalColumnWeight = 76 + columnWidths.reduce((total, width) => total + width, 0);
    const headerGridColumns = `76fr ${columnWidths.map((width) => `${width}fr`).join(' ')}`;
    const getRowKey = (row: EditableRecord, index: number) => asText(row._clientId) || `row-${index}`;

    const toggleEditRow = (rowKey: string) => {
        setEditingRows((current) => {
            const next = new Set(current);
            if (next.has(rowKey)) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }
            return next;
        });
    };

    const updateRow = (rowIndex: number, key: string, value: unknown) => {
        onRowsChange(rows.map((row, index) => (index === rowIndex ? { ...row, [key]: value } : row)));
    };

    const deleteRow = (rowIndex: number) => {
        const rowKey = getRowKey(rows[rowIndex], rowIndex);
        setEditingRows((current) => {
            const next = new Set(current);
            next.delete(rowKey);
            return next;
        });
        onRowsChange(rows.filter((_row, index) => index !== rowIndex));
    };

    return (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'visible', minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    minWidth: 0,
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    borderBottom: '1px solid rgba(148, 163, 184, 0.24)',
                    backgroundColor: '#f8fafc',
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 850 }}>
                        {label}
                    </Typography>
                    <Chip size="small" label={`${rows.length} rows`} />
                </Stack>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ minWidth: 0 }}>
                    <Button startIcon={<AddRoundedIcon />} variant="outlined" onClick={onAdd}>
                        Add Row
                    </Button>
                    <Button startIcon={<SaveRoundedIcon />} variant="contained" disabled={isSaving} onClick={onSave}>
                        Save
                    </Button>
                </Stack>
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: headerGridColumns,
                    position: 'sticky',
                    top: 0,
                    zIndex: 12,
                    width: '100%',
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid rgba(148, 163, 184, 0.32)',
                    boxShadow: '0 2px 0 rgba(15, 23, 42, 0.04)',
                }}
            >
                <Box sx={{ px: 1.25, py: 1.25 }} />
                {columns.map((column) => (
                    <Box
                        key={column.key}
                        sx={{
                            px: 1.25,
                            py: 1.25,
                            color: '#111827',
                            fontWeight: 850,
                            lineHeight: 1.25,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                        }}
                    >
                        {column.label}
                    </Box>
                ))}
            </Box>
            <TableContainer
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    overflowX: 'visible',
                    overflowY: 'visible',
                }}
            >
                <Table
                    size="small"
                    sx={{
                        width: '100%',
                        minWidth: '100%',
                        tableLayout: 'fixed',
                        '& .MuiTableCell-root': {
                            px: 1.25,
                            py: 1,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                        },
                    }}
                >
                    <colgroup>
                        <col style={{ width: `${(76 / totalColumnWeight) * 100}%` }} />
                        {columns.map((column, index) => (
                            <col key={column.key} style={{ width: `${(columnWidths[index] / totalColumnWeight) * 100}%` }} />
                        ))}
                    </colgroup>
                    <TableBody>
                        {rows.map((row, rowIndex) => (
                            <TableRow key={row._clientId ?? rowIndex} hover>
                                <TableCell>
                                    <Stack direction="row" spacing={0.5}>
                                        <Tooltip title={editingRows.has(getRowKey(row, rowIndex)) ? 'Done editing row' : 'Edit row'}>
                                            <IconButton size="small" color="primary" onClick={() => toggleEditRow(getRowKey(row, rowIndex))}>
                                                {editingRows.has(getRowKey(row, rowIndex)) ? <CheckRoundedIcon fontSize="small" /> : <EditRoundedIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete row">
                                            <IconButton size="small" color="error" onClick={() => deleteRow(rowIndex)}>
                                                <DeleteOutlineRoundedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                                {columns.map((column) => {
                                    const isEditing = editingRows.has(getRowKey(row, rowIndex));
                                    return (
                                        <TableCell
                                            key={`${row._clientId}-${column.key}`}
                                            sx={{
                                                width: getDisplayColumnWidth(section, column),
                                                minWidth: getDisplayColumnWidth(section, column),
                                                verticalAlign: 'top',
                                            }}
                                        >
                                            {isEditing
                                                ? renderEditableCell(row, column, (key, value) => updateRow(rowIndex, key, value))
                                                : renderReadonlyCell(row, column)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + 1}>
                                    <Typography sx={{ py: 5, textAlign: 'center', color: '#64748b' }}>
                                        No rows loaded for this collection.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : null}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

function CreateProfileDialog({
    open,
    onClose,
    onCreate,
}: {
    open: boolean;
    onClose: () => void;
    onCreate: (input: { profileName: string; description: string }) => Promise<void>;
}) {
    const [profileName, setProfileName] = useState('');
    const [description, setDescription] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        if (!open) return;
        setProfileName('');
        setDescription('');
    }, [open]);

    const handleCreate = async () => {
        if (!profileName.trim()) return;
        setIsCreating(true);
        try {
            await onCreate({ profileName: profileName.trim(), description: description.trim() });
            onClose();
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Create Study Plan Profile</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <TextField
                        label="Profile Name"
                        value={profileName}
                        onChange={(event) => setProfileName(event.target.value)}
                        fullWidth
                        autoFocus
                    />
                    <TextField
                        label="Description"
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={isCreating || !profileName.trim()} onClick={() => void handleCreate()}>
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export function StudyPlanAlgorithmPage() {
    const notify = useNotify();
    const navigate = useNavigate();
    const { section } = useParams();
    const activeSection = getSectionFromParam(section);

    const [profiles, setProfiles] = useState<AdminStudyPlanProfile[]>([]);
    const [selectedProfileId, setSelectedProfileId] = useState('');
    const [detail, setDetail] = useState<AdminStudyPlanProfileDetail | null>(null);
    const [drafts, setDrafts] = useState<Record<EditableConfigSection, EditableRecord[]>>(emptyDrafts);
    const [validation, setValidation] = useState<AdminStudyPlanValidation | null>(null);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [examKindFilter, setExamKindFilter] = useState<ExamKindFilter>('');
    const [skillFilter, setSkillFilter] = useState<SkillFilter>('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSectionLoading, setIsSectionLoading] = useState(false);
    const [loadingSectionLabel, setLoadingSectionLabel] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [loadError, setLoadError] = useState('');

    const selectedProfile = detail?.profile ?? profiles.find((profile) => profile.studyPlanAlgorithmProfileId === selectedProfileId) ?? null;
    const selectedSectionMeta = getCollectionMeta(activeSection);
    const editableSection = activeSection === 'profiles' ? null : activeSection;
    const activeRows = editableSection ? getFilteredRows(editableSection, drafts[editableSection], examKindFilter, skillFilter, detail) : [];
    const activeColumns = editableSection ? getColumns(editableSection, detail) : [];
    const activeSectionLabel = selectedSectionMeta?.label ?? 'Configuration';
    const showSectionLoading = isSectionLoading || (isLoading && Boolean(selectedProfileId));

    const profileOptions = useMemo(() => {
        return profiles.map((profile) => ({
            value: profile.studyPlanAlgorithmProfileId,
            label: `${profile.profileName} v${profile.versionNumber} (${profile.status})`,
        }));
    }, [profiles]);

    async function loadProfiles(preferredProfileId?: string) {
        setIsLoading(true);
        setLoadError('');
        try {
            const response = await getAdminStudyPlanProfiles({ page: 1, pageSize: 100, search, status });
            setProfiles(response.items);
            const preferred = response.items.find((profile) => profile.studyPlanAlgorithmProfileId === preferredProfileId);
            const active = response.items.find((profile) => profile.status === 'Active' && profile.isDefault);
            const fallback = preferred ?? active ?? response.items[0] ?? null;
            setSelectedProfileId(fallback?.studyPlanAlgorithmProfileId ?? '');
            if (!fallback) {
                setDetail(null);
                setDrafts(emptyDrafts);
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to load study plan profiles';
            setLoadError(message);
            notify(message, { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    }

    async function loadProfile(profileId: string) {
        if (!profileId) return;
        setIsLoading(true);
        setLoadError('');
        try {
            const nextDetail = await getAdminStudyPlanProfile(profileId);
            setDetail(nextDetail);
            setDrafts({
                skillMarkRanges: withClientIds(nextDetail.skillMarkRanges),
                taskRules: withClientIds(nextDetail.taskRules),
                taskDailyRequirements: withClientIds(nextDetail.taskDailyRequirements),
                taskSkillWeights: withClientIds(nextDetail.taskSkillWeights),
                multiplierRules: withClientIds(nextDetail.multiplierRules),
                mockRules: withClientIds(nextDetail.mockRules),
                taskScoreThresholds: withClientIds(nextDetail.taskScoreThresholds),
                readinessWeights: withClientIds(nextDetail.readinessWeights),
                readinessStatuses: withClientIds(nextDetail.readinessStatuses),
            });
            setValidation(null);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to load study plan profile detail';
            setLoadError(message);
            notify(message, { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        void loadProfiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedProfileId) return;
        void loadProfile(selectedProfileId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProfileId]);

    useEffect(() => {
        if (section && activeSection === section) return;
        navigate(`/study-plan-algorithm/${activeSection}`, { replace: true });
    }, [activeSection, navigate, section]);

    useEffect(() => {
        setLoadingSectionLabel(activeSectionLabel);
        setIsSectionLoading(true);
        const timer = window.setTimeout(() => setIsSectionLoading(false), 180);
        return () => window.clearTimeout(timer);
    }, [activeSection, activeSectionLabel]);

    const setSectionRows = (sectionKey: EditableConfigSection, rows: EditableRecord[]) => {
        setDrafts((current) => ({
            ...current,
            [sectionKey]: mergeFilteredRows(sectionKey, current[sectionKey], rows, examKindFilter, skillFilter, detail),
        }));
    };

    const handleRefresh = () => {
        void loadProfiles(selectedProfileId);
    };

    const handleSectionChange = (nextValue: ConfigSection) => {
        if (nextValue === activeSection) return;
        const nextLabel = configSections.find((item) => item.key === nextValue)?.label ?? 'Configuration';
        setLoadingSectionLabel(nextLabel);
        setIsSectionLoading(true);
        navigate(`/study-plan-algorithm/${nextValue}`);
    };

    const handleCreateProfile = async (input: { profileName: string; description: string }) => {
        try {
            const created = await createAdminStudyPlanProfile(input);
            notify('Study plan profile created', { type: 'info' });
            await loadProfiles(created.studyPlanAlgorithmProfileId);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to create profile';
            notify(message, { type: 'error' });
            throw error;
        }
    };

    const handleClone = async () => {
        if (!selectedProfileId) return;
        try {
            const cloned = await cloneAdminStudyPlanProfile(selectedProfileId);
            notify('Study plan profile cloned', { type: 'info' });
            await loadProfiles(cloned.studyPlanAlgorithmProfileId);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to clone profile';
            notify(message, { type: 'error' });
        }
    };

    const handlePublish = async () => {
        if (!selectedProfileId) return;
        try {
            const published = await publishAdminStudyPlanProfile(selectedProfileId);
            notify('Study plan profile published', { type: 'info' });
            await loadProfiles(published.studyPlanAlgorithmProfileId);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to publish profile';
            notify(message, { type: 'error' });
        }
    };

    const handleArchive = async () => {
        if (!selectedProfileId) return;
        if (!window.confirm('Archive this study plan algorithm profile?')) return;
        try {
            await archiveAdminStudyPlanProfile(selectedProfileId);
            notify('Study plan profile archived', { type: 'info' });
            await loadProfiles();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to archive profile';
            notify(message, { type: 'error' });
        }
    };

    const handleValidate = async () => {
        if (!selectedProfileId) return;
        try {
            const result = await validateAdminStudyPlanProfile(selectedProfileId);
            setValidation(result);
            notify(result.isValid ? 'Profile validation passed' : 'Profile validation returned issues', {
                type: result.isValid ? 'info' : 'warning',
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to validate profile';
            notify(message, { type: 'error' });
        }
    };

    const handleSaveSection = async (sectionKey: EditableConfigSection) => {
        if (!selectedProfileId) return;

        setIsSaving(true);
        try {
            let nextDetail: AdminStudyPlanProfileDetail;
            switch (sectionKey) {
                case 'skillMarkRanges':
                    nextDetail = await upsertAdminStudyPlanCollection(
                        selectedProfileId,
                        'skillMarkRanges',
                        stripClientFields<AdminStudyPlanSkillMarkRangeRule>(drafts.skillMarkRanges),
                    );
                    break;
                case 'taskRules':
                    nextDetail = await upsertAdminStudyPlanCollection(
                        selectedProfileId,
                        'taskRules',
                        stripClientFields<AdminStudyPlanTaskRule>(drafts.taskRules),
                    );
                    break;
                case 'taskDailyRequirements':
                    nextDetail = await upsertAdminStudyPlanCollection(
                        selectedProfileId,
                        'taskDailyRequirements',
                        stripClientFields<AdminStudyPlanTaskDailyRequirementRule>(drafts.taskDailyRequirements),
                    );
                    break;
                case 'taskSkillWeights':
                    nextDetail = await upsertAdminStudyPlanCollection(
                        selectedProfileId,
                        'taskSkillWeights',
                        stripClientFields<AdminStudyPlanTaskSkillWeight>(drafts.taskSkillWeights),
                    );
                    break;
                case 'multiplierRules':
                    nextDetail = await upsertAdminStudyPlanCollection(
                        selectedProfileId,
                        'multiplierRules',
                        stripClientFields<AdminStudyPlanMultiplierRule>(drafts.multiplierRules),
                    );
                    break;
                case 'mockRules':
                    nextDetail = await upsertAdminStudyPlanCollection(
                        selectedProfileId,
                        'mockRules',
                        stripClientFields<AdminStudyPlanMockRule>(drafts.mockRules),
                    );
                    break;
                case 'taskScoreThresholds':
                    nextDetail = await upsertAdminStudyPlanCollection(
                        selectedProfileId,
                        'taskScoreThresholds',
                        stripClientFields<AdminStudyPlanTaskScoreThresholdRule>(drafts.taskScoreThresholds),
                    );
                    break;
                case 'readinessWeights':
                    nextDetail = await upsertAdminStudyPlanCollection(
                        selectedProfileId,
                        'readinessWeights',
                        stripClientFields<AdminStudyPlanReadinessWeightRule>(drafts.readinessWeights),
                    );
                    break;
                case 'readinessStatuses':
                    nextDetail = await upsertAdminStudyPlanCollection(
                        selectedProfileId,
                        'readinessStatuses',
                        stripClientFields<AdminStudyPlanReadinessStatusRule>(drafts.readinessStatuses),
                    );
                    break;
            }

            setDetail(nextDetail);
            setDrafts({
                skillMarkRanges: withClientIds(nextDetail.skillMarkRanges),
                taskRules: withClientIds(nextDetail.taskRules),
                taskDailyRequirements: withClientIds(nextDetail.taskDailyRequirements),
                taskSkillWeights: withClientIds(nextDetail.taskSkillWeights),
                multiplierRules: withClientIds(nextDetail.multiplierRules),
                mockRules: withClientIds(nextDetail.mockRules),
                taskScoreThresholds: withClientIds(nextDetail.taskScoreThresholds),
                readinessWeights: withClientIds(nextDetail.readinessWeights),
                readinessStatuses: withClientIds(nextDetail.readinessStatuses),
            });
            notify('Study plan algorithm config saved', { type: 'info' });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to save study plan config';
            notify(message, { type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const totalConfigRows = detail
        ? detail.skillMarkRanges.length +
          detail.taskRules.length +
          detail.taskDailyRequirements.length +
          detail.taskSkillWeights.length +
          detail.multiplierRules.length +
          detail.mockRules.length +
          detail.taskScoreThresholds.length +
          detail.readinessWeights.length +
          detail.readinessStatuses.length
        : 0;

    return (
        <Card sx={{ borderRadius: 3, boxShadow: '0 18px 54px rgba(15, 23, 42, 0.10)', overflow: 'visible', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
            <GlobalStyles
                styles={{
                    body: {
                        overflowX: 'visible !important',
                        overflowY: 'visible !important',
                    },
                    '#root': {
                        overflowX: 'visible !important',
                        overflowY: 'visible !important',
                    },
                }}
            />
            <Title title="Study Plan Algorithm" />
            {isLoading || isSaving ? <LinearProgress /> : null}
            <CardContent sx={{ p: { xs: 2, md: 3 }, minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'visible' }}>
                <Stack spacing={2.5} sx={{ minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: { xs: 'stretch', lg: 'center' },
                                justifyContent: 'space-between',
                                flexDirection: { xs: 'column', lg: 'row' },
                                minWidth: 0,
                                width: '100%',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                            }}
                        >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h4" sx={{ color: '#0f172a', fontWeight: 900, lineHeight: 1.1 }}>
                                Study Plan Algorithm
                            </Typography>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                                {selectedProfile ? <StatusChip status={selectedProfile.status} /> : null}
                                {selectedProfile?.isDefault ? <Chip size="small" color="primary" label="Default" /> : null}
                                {selectedProfile ? <Chip size="small" variant="outlined" label={`v${selectedProfile.versionNumber}`} /> : null}
                            </Stack>
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button startIcon={<RefreshRoundedIcon />} variant="outlined" onClick={handleRefresh}>
                                Refresh
                            </Button>
                            <Button startIcon={<AddRoundedIcon />} variant="contained" onClick={() => setCreateOpen(true)}>
                                New Profile
                            </Button>
                        </Stack>
                    </Box>

                    {loadError ? <Alert severity="error">{loadError}</Alert> : null}

                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: {
                                xs: '1fr',
                                md: 'repeat(2, minmax(0, 1fr))',
                                xl: 'repeat(4, minmax(0, 1fr))',
                            },
                            minWidth: 0,
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        <SummaryCard label="Profiles" value={`${profiles.length}`} helper="Draft, active, and archived algorithm versions" />
                        <SummaryCard label="Config Rows" value={`${totalConfigRows}`} helper="Rules loaded for the selected profile" />
                        <SummaryCard
                            label="Validation"
                            value={validation ? (validation.isValid ? 'Pass' : 'Issues') : 'Not run'}
                            helper={validation ? `${validation.errors.length} errors, ${validation.warnings.length} warnings` : 'Run validation before publishing'}
                        />
                        <SummaryCard
                            label="Selected"
                            value={selectedProfile?.profileName ?? 'None'}
                            helper={selectedProfile ? `Updated ${formatApiDateTime(selectedProfile.updatedAt)}` : 'No profile selected'}
                        />
                    </Box>

                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'clip' }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 2,
                                gridTemplateColumns: { xs: '1fr', xl: 'minmax(320px, 420px) minmax(0, 1fr)' },
                                minWidth: 0,
                                width: '100%',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Stack spacing={2} sx={{ minWidth: 0 }}>
                                <SegmentedTabs
                                    label="Profile Status"
                                    value={status}
                                    options={[
                                        { value: '', label: 'All' },
                                        { value: 'Draft', label: 'Draft' },
                                        { value: 'Active', label: 'Active' },
                                        { value: 'Archived', label: 'Archived' },
                                    ]}
                                    onChange={setStatus}
                                />
                                <TextField
                                    label="Search profiles"
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') void loadProfiles(selectedProfileId);
                                    }}
                                    fullWidth
                                />
                                <TextField
                                    select
                                    label="Selected profile"
                                    value={selectedProfileId}
                                    onChange={(event) => setSelectedProfileId(event.target.value)}
                                    fullWidth
                                >
                                    {profileOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                            <Stack spacing={1} sx={{ minWidth: 0 }}>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ minWidth: 0 }}>
                                    <Button startIcon={<ContentCopyRoundedIcon />} variant="outlined" disabled={!selectedProfileId} onClick={() => void handleClone()}>
                                        Clone
                                    </Button>
                                    <Button startIcon={<FactCheckOutlinedIcon />} variant="outlined" disabled={!selectedProfileId} onClick={() => void handleValidate()}>
                                        Validate
                                    </Button>
                                    <Button startIcon={<PublishRoundedIcon />} variant="contained" disabled={!selectedProfileId} onClick={() => void handlePublish()}>
                                        Publish
                                    </Button>
                                    <Button startIcon={<ArchiveOutlinedIcon />} color="warning" variant="outlined" disabled={!selectedProfileId} onClick={() => void handleArchive()}>
                                        Archive
                                    </Button>
                                </Stack>
                                {validation ? (
                                    <Alert severity={validation.isValid ? 'success' : 'warning'}>
                                        <Stack spacing={0.5}>
                                            <Typography sx={{ fontWeight: 800 }}>
                                                {validation.isValid ? 'Profile is valid' : 'Profile needs attention'}
                                            </Typography>
                                            {[...validation.errors, ...validation.warnings].slice(0, 6).map((message) => (
                                                <Typography key={message} variant="body2">
                                                    {message}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    </Alert>
                                ) : null}
                            </Stack>
                        </Box>
                    </Paper>

                    <Box sx={{ minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
                        <Box
                            sx={{
                                mb: 1.5,
                                display: 'grid',
                                gap: 1.5,
                                gridTemplateColumns: { xs: '1fr', xl: 'minmax(320px, 460px) minmax(420px, 620px)' },
                                alignItems: 'end',
                            }}
                        >
                            <SegmentedTabs
                                label="Exam"
                                value={examKindFilter}
                                options={examKindFilterOptions}
                                onChange={(value) => setExamKindFilter(value as ExamKindFilter)}
                            />
                            <SegmentedTabs
                                label="Skill"
                                value={skillFilter}
                                options={skillFilterOptions}
                                onChange={(value) => setSkillFilter(value as SkillFilter)}
                            />
                        </Box>
                        <Tabs
                            value={activeSection}
                            onChange={(_event, nextValue: ConfigSection) => handleSectionChange(nextValue)}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            sx={{
                                minWidth: 0,
                                width: '100%',
                                maxWidth: '100%',
                                '& .MuiTabs-scroller': {
                                    overflowX: 'auto !important',
                                },
                                '& .MuiTabs-flexContainer': {
                                    minWidth: 'max-content',
                                },
                                '& .MuiTab-root': {
                                    flexShrink: 0,
                                    minWidth: { xs: 112, sm: 128 },
                                    px: { xs: 1.5, sm: 2 },
                                    whiteSpace: 'nowrap',
                                },
                            }}
                        >
                            {configSections.map((item) => (
                                <Tab key={item.key} value={item.key} label={item.label} />
                            ))}
                        </Tabs>
                    </Box>

                    {showSectionLoading ? (
                        <SectionLoadingPanel label={loadingSectionLabel || activeSectionLabel} />
                    ) : null}

                    {!showSectionLoading && activeSection === 'profiles' ? (
                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                            <TableContainer sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto' }}>
                                <Table size="small" sx={{ minWidth: 760 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 800 }}>Profile</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>Version</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>Published</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>Updated</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {profiles.map((profile) => (
                                            <TableRow
                                                key={profile.studyPlanAlgorithmProfileId}
                                                hover
                                                selected={profile.studyPlanAlgorithmProfileId === selectedProfileId}
                                                onClick={() => setSelectedProfileId(profile.studyPlanAlgorithmProfileId)}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <TableCell>
                                                    <Stack spacing={0.25}>
                                                        <Typography sx={{ fontWeight: 800 }}>{profile.profileName}</Typography>
                                                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                                                            {profile.description || profile.studyPlanAlgorithmProfileId}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <StatusChip status={profile.status} />
                                                </TableCell>
                                                <TableCell>{profile.versionNumber}</TableCell>
                                                <TableCell>{profile.publishedAt ? formatApiDateTime(profile.publishedAt) : '-'}</TableCell>
                                                <TableCell>{formatApiDateTime(profile.updatedAt)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    ) : null}

                    {!showSectionLoading && editableSection && selectedSectionMeta?.collection ? (
                        detail ? (
                            <ConfigTable
                                section={editableSection}
                                rows={activeRows}
                                columns={activeColumns}
                                isSaving={isSaving}
                                onRowsChange={(rows) => setSectionRows(editableSection, rows)}
                                onAdd={() => setSectionRows(editableSection, [...activeRows, createDefaultRow(editableSection, detail, examKindFilter)])}
                                onSave={() => void handleSaveSection(editableSection)}
                            />
                        ) : (
                            <Alert severity="info">Select a profile to edit this collection.</Alert>
                        )
                    ) : null}
                </Stack>
            </CardContent>
            <CreateProfileDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreate={handleCreateProfile}
            />
            <Divider />
        </Card>
    );
}
